import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { ButtonSizeOptions, InputTypeAhead, InputNumber, ButtonIcon } from "@loanbossinc/component-library";
import { withFormik } from "formik";

import { normalizeDate } from "../../lenderReportingDashboard/constants";

import {
  BackButton,
  BackDeleteWrapper,
  DatePicker,
  DealName,
  InputName,
  InputWrapper,
  StyledDeleteButton,
  StyledPrimaryButton,
  Title,
  Wrapper
} from "./styles";

const FREQUENCY_OPTIONS = [
  { name: "Monthly", id: "monthly", dbId: "bbd95d5d-4688-11e9-82f6-0242ac120002" },
  { name: "Quarterly", id: "quarterly", dbId: "d019a5e2-4688-11e9-82f6-0242ac120002" },
  { name: "Semi-Annually", id: "semi-annually", dbId: "d41fb593-4688-11e9-82f6-0242ac120002" },
  { name: "Annually", id: "annually", dbId: "d749ecb6-4688-11e9-82f6-0242ac120002" }
];

/*
 * Missing:
 * Bi-Monthly	bf12a607-4688-11e9-82f6-0242ac120002
 * None	eefe2b88-4688-11e9-82f6-0242ac120002
 */

const ValidationError = ({ message }) => (
  <div style={{ position: "absolute", color: "#FF1744", fontFamily: "Work Sans", fontSize: "12px", top: "60px" }}>{message}</div>
);

const Step2 = props => {
  const {
    deal,
    handleSubmit,
    values,
    setFieldValue,
    wizardStepDecrement,
    reportConfig,
    reportSelections,
    selectedReportId,
    history,
    isEditing,
    setSelections,
    validationError
  } = props;
  const { firstReportDueDate, lastReportDueDate, dataAsOfDate, frequency } = values;
  const canCustomizeCalculations = moment(firstReportDueDate).isValid() && moment(lastReportDueDate).isValid() && moment(dataAsOfDate).isValid() && Boolean(frequency);

  const [lastDueValues, setLastDueValues] = useState({
    month: null,
    day: null,
    year: null,
    date: null
  });

  const asOfDateRef = useRef();
  const firstDueDateRef = useRef();

  useEffect(() => {
    if (reportConfig.lastReportDueDate) {
      const month = moment.utc(reportConfig.lastReportDueDate).format("MMMM");
      const year = moment.utc(reportConfig.lastReportDueDate).year();
      const day = moment.utc(reportConfig.lastReportDueDate).date().toString();
      const formattedMonth = Number(moment().month(month).format("M"));
      const lastDayOfMonth = new Date(year, formattedMonth, 0).getDate();
      const lastDayOfMonthDate = moment.utc([year, formattedMonth - 1, lastDayOfMonth]);

      setLastDueValues({ month, day, year, date: lastDayOfMonthDate });
      setFieldValue("lastReportDueDate", lastDayOfMonthDate);
    }
  }, [reportConfig.lastReportDueDate]);

  useEffect(() => {
    if (reportSelections.selectedDeliverableId) {
      setFieldValue("selectedDeliverableId", reportSelections.selectedDeliverableId);
    }

    if (reportSelections.reportingFrequencyId) {
      const options = FREQUENCY_OPTIONS.filter(option => option.dbId === reportSelections.reportingFrequencyId);
      if (options.length > 0) {
        setFrequency(options[0].id);
      }
    }
  }, [reportSelections]);

  useEffect(() => {
    if (selectedReportId === "new") {
      history.push(`/deal/${reportConfig.dealId}/lender-reports-setup/${reportConfig.id}`, { from: "undefined-7" });
    }

    if (reportConfig.reportGroupInstances && reportConfig.reportGroupInstances.length > 0) {
      const reportGroupInstances = reportConfig.reportGroupInstances
        .sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate)))
        .filter(reportGroupInstance => isDateAfterOrEqualToToday(reportGroupInstance.dueDate));

      if (reportGroupInstances.length > 0) {
        history.push(`/lenderReportingDashboard/tasks/${reportGroupInstances[0].id}`, { from: "step2" });
      } else {
        history.push(`/deal/${reportConfig.dealId}/lender-reports/${selectedReportId}`, { from: "step2" });
      }
    }
  }, [reportConfig]);

  useEffect(() => {
    const hasValidFirstReportDueDate = (firstReportDueDate && moment(firstReportDueDate).isValid()) || (reportSelections.reportingStartDate && moment(reportSelections.reportingStartDate).isValid());
    const hasValidLastReportDueDateYearAndMonth = moment(reportSelections.reportingEndDate).isValid() || (lastDueValues?.month && lastDueValues?.year);

    if (hasValidFirstReportDueDate && hasValidLastReportDueDateYearAndMonth) {
      const dayOfFirstReportDueDate = moment.utc(firstReportDueDate).date();
      const lastDueDateYear = Number(lastDueValues.year);
      const lastDueDateMonth = lastDueValues.month;
      const formattedMonth = lastDueValues.month ? Number(moment().month(lastDueDateMonth).format("M")) : null;
      const lastDueDateString = `${lastDueDateYear} ${formattedMonth} ${dayOfFirstReportDueDate}`;
      const momentlastDueDateString = moment(lastDueDateString, "YYYY MM DD");
      const isValidLastDueDate = momentlastDueDateString.isValid();

      /*
       * If someone changes the first report due date to a day that doesn't exist in the last report due month, then we will default to the last day of the report month
       * Example - if the first report due date is Mar 31 and last report due month is Feb, there is no Feb 31. Will default to Feb 28.
       */
      const dayOfLastReportDueDate = !isValidLastDueDate ? new Date(lastDueDateYear, formattedMonth, 0).getDate() : dayOfFirstReportDueDate;

      const lastDayOfMonthDate = normalizeDate(moment.utc([lastDueDateYear, formattedMonth - 1, dayOfLastReportDueDate]));
      setFieldValue("lastReportDueDate", lastDayOfMonthDate);
      setLastDueValues(v => ({ ...v, day: dayOfLastReportDueDate.toString() }));
    }
  }, [firstReportDueDate]);

  const isDateAfterOrEqualToToday = date => {
    const today = moment();
    const dateToCompare = moment(date);

    return dateToCompare.isAfter(today) || dateToCompare.isSame(today);
  };

  const setFrequency = value => setFieldValue("frequency", value ? value : null);

  const handleDateChange = (name, value) => {
    if (value) {
      // set asOfDate to last day of month
      if (name === "dataAsOfDate") {
        const month = Number(
          moment()
            .month(asOfDateRef.current.state?.month)
            .format("M")
        );
        const year = Number(asOfDateRef.current.state?.year);
        const lastDayInMonth = (Boolean(year) && Boolean(month)) ? new Date(year, month, 0).getDate() : 28;
        const lastDayOfMonthDate = normalizeDate(moment.utc([year, month - 1, lastDayInMonth]));
        setFieldValue(name, lastDayOfMonthDate);
      } else {
        setFieldValue(name, moment.utc(value));
      }
    } else {
      setFieldValue(name, null);
    }
  };

  const handleLastDueYearChange = e => {
    const year = Number(e?.target?.value ?? null);
    if (year.toString().length === 4) {
      const month = lastDueValues.month ? Number(
        moment()
          .month(lastDueValues.month)
          .format("M")
      ) : null;
      const lastDayInMonth = (Boolean(year) && Boolean(month)) ? new Date(year, month, 0).getDate() : 28;
      const lastDayOfMonthDate = normalizeDate(moment.utc([year, month - 1, lastDayInMonth]));
      setFieldValue("lastReportDueDate", lastDayOfMonthDate);
      setLastDueValues(v => ({ ...v, day: lastDayInMonth.toString(), year: Number(e?.target?.value) }));
    }
  };

  const handleLastDueMonthChange = value => {
    const year = Number(lastDueValues.year);
    const month = value ? Number(
      moment()
        .month(value)
        .format("M")
    ) : null;
    const lastDayInMonth = (Boolean(year) && Boolean(month)) ? new Date(year, month, 0).getDate() : 28;
    const lastDayOfMonthDate = normalizeDate(moment.utc([year, month - 1, lastDayInMonth]));
    setFieldValue("lastReportDueDate", lastDayOfMonthDate);
    setLastDueValues(v => ({ ...v, day: lastDayInMonth.toString(), month: value }));
  };

  const clearLastDueInputs = () => {
    setLastDueValues({ day: null, month: null, year: "" });
  };

  const handleGoBack = () => {
    wizardStepDecrement();
  };

  const handleDelete = () => {
    setSelections({ isDeleteDialogOpen: true, wizardStep: 0 });
  };

  return (
    <>
      <Title>Set up lender compliance reports for</Title>
      <DealName>{deal && deal.name}</DealName>
      <InputWrapper>
        <InputName value="First Report is Due" />
        <DatePicker
          ref={firstDueDateRef}
          id="first-due-date"
          name="first-due-date"
          label="first-due-date"
          variant="filled"
          date={firstReportDueDate}
          onChange={value => handleDateChange("firstReportDueDate", value)}
        />
        <InputName value="The data will be as of" />
        <div style={{ display: "flex", position: "relative" }}>
          <DatePicker
            ref={asOfDateRef}
            id="as-of-date"
            name="as-of-date"
            label="as-of-date"
            variant="filled"
            date={dataAsOfDate}
            onChange={value => handleDateChange("dataAsOfDate", value)}
            hideDay
          />
          {validationError && validationError.asOfDate && <ValidationError message={validationError.asOfDate} />}
        </div>
      </InputWrapper>
      <InputWrapper>
        <InputName value="I want to generate this report" />
        <InputTypeAhead
          variant="filled"
          id="frequency"
          name="Frequency"
          label="Frequency"
          options={FREQUENCY_OPTIONS}
          displayWith={option => option && option.name}
          setValueWith={option => option && option.id}
          onChange={setFrequency}
          value={frequency}
        />
        <InputName value="The last report will be due" />
        <Wrapper>
          <InputTypeAhead
            style={{ minWidth: 176 }}
            name="month"
            label="Month"
            options={moment.months()}
            onChange={handleLastDueMonthChange}
            value={lastDueValues?.month}
            disableClearable
            variant="filled"
          />

          <InputTypeAhead
            className="ml-2"
            style={{ width: 80, marginLeft: ".5em" }}
            name="day"
            label="Day"
            value={lastDueValues?.day}
            disableClearable
            disabled
            variant="filled"
          />

          <InputNumber
            className="ml-2"
            style={{ width: 80, marginLeft: ".5em" }}
            name="year"
            label="Year"
            format="####"
            value={lastDueValues?.year}
            onChange={event => handleLastDueYearChange(event)}
            variant="filled"
          />
          <ButtonIcon title="Clear Date" buttonSize="25" iconName="clear" style={{ marginLeft: "8px", top: "32px" }} onClick={clearLastDueInputs} />
          {validationError && validationError.lastDueDate && <ValidationError message={validationError.lastDueDate} />}
        </Wrapper>
      </InputWrapper>
      <StyledPrimaryButton
        disabled={!canCustomizeCalculations}
        onClick={handleSubmit}
        size={ButtonSizeOptions.lg}
        label="Done"
        style={{ width: "100%" }}
      />
      <BackDeleteWrapper>
        <BackButton onClick={handleGoBack} id="back-button" label="Report name and context" iconName="arrow_forward" reverse alignRight={false} key={1} />
        {isEditing && <StyledDeleteButton label="Discard report" onClick={handleDelete} />}
      </BackDeleteWrapper>
    </>
  );
};

export default withFormik({
  mapPropsToValues: ({ reportConfig }) => ({ ...reportConfig }),
  handleSubmit(values, { props, setSubmitting }) {
    const { history, postReportConfig, setSelections } = props;
    const { firstReportDueDate, lastReportDueDate, dataAsOfDate } = values;

    if (moment(dataAsOfDate).isSameOrAfter(firstReportDueDate) || moment(firstReportDueDate).isSameOrAfter(lastReportDueDate)) {
      setSelections({
        validationError: {
          asOfDate: moment(dataAsOfDate).isSameOrAfter(firstReportDueDate) ? "The As of Date must be prior to First Report Date" : null,
          lastDueDate: moment(firstReportDueDate).isSameOrAfter(lastReportDueDate) ? "The Last Report Due Date must be after First Report Due Date" : null
        }
      });
    } else {
      postReportConfig({ data: { ...values } });
      setSubmitting(false);
      setSelections({
        validationError: {
          asOfDate: null,
          lastDueDate: null
        }
      });
    }
  }
})(Step2);
